<template>
  <div class="container py-4">
    <div class="page-card">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 class="mb-0 section-title-accent">POS quick keys</h4>
          <p class="small text-muted mb-0 mt-1">
            Pin up to 12 products on the POS screen for one-tap add-to-cart.
          </p>
        </div>
        <div class="d-flex gap-2">
          <icon-button
            v-if="pinnedProducts.length"
            :func="clearAll"
            :icon="'trash'"
            :title="'Use auto suggestions'"
            :classes="'akkurate-gray-btn btn-size2'"
            :btnName="'Use auto'"
            :disabled="saving"
          />
          <icon-button
            :func="save"
            :icon="'save'"
            :title="'Save quick keys'"
            :classes="'akkurate-green-btn btn-size2'"
            :btnName="'Save'"
            :disabled="saving || !selectedStoreId"
          />
        </div>
      </div>

      <b-alert v-if="!selectedStoreId" show variant="warning" class="small">
        Select a store from the sidebar to configure quick keys.
      </b-alert>

      <b-alert
        v-else-if="pinnedProducts.length"
        show
        variant="light"
        class="small"
      >
        {{ pinnedProducts.length }} product{{
          pinnedProducts.length === 1 ? "" : "s"
        }}
        selected — click Save to apply on POS.
      </b-alert>
      <b-alert v-else-if="source === 'auto'" show variant="info" class="small">
        No pinned keys yet — POS shows top sellers from the last 30 days. Pin
        products below to override.
      </b-alert>
      <b-alert v-else show variant="success" class="small">
        Pinned layout active on POS for this store.
      </b-alert>

      <div class="quick-keys-admin">
        <section class="quick-keys-admin__panel">
          <h6 class="quick-keys-admin__heading">
            Pinned products ({{ pinnedProducts.length }}/12)
          </h6>
          <div v-if="!pinnedProducts.length" class="text-muted small py-3">
            Drag order with arrows. Add products from search on the right.
          </div>
          <ul v-else class="quick-keys-admin__list">
            <li
              v-for="(product, index) in pinnedProducts"
              :key="product.id"
              class="quick-keys-admin__item"
            >
              <span class="quick-keys-admin__order">{{ index + 1 }}</span>
              <div class="quick-keys-admin__info">
                <strong>{{ product.name }}</strong>
                <span class="small text-muted d-block">{{ product.sku }}</span>
              </div>
              <span class="quick-keys-admin__price">{{
                formatMoney(product.unitPrice)
              }}</span>
              <div class="quick-keys-admin__actions">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="index === 0"
                  @click="moveUp(index)"
                >
                  ↑
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="index === pinnedProducts.length - 1"
                  @click="moveDown(index)"
                >
                  ↓
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="removeAt(index)"
                >
                  ×
                </b-button>
              </div>
            </li>
          </ul>
        </section>

        <section class="quick-keys-admin__panel">
          <h6 class="quick-keys-admin__heading">Add product</h6>
          <b-form-input
            v-model="search"
            placeholder="Search by name, SKU, or barcode…"
            class="mb-2 form-control"
            @input="debouncedSearch"
          />
          <div v-if="searchLoading" class="small text-muted py-2">
            Searching…
          </div>
          <ul v-else-if="searchResults.length" class="quick-keys-admin__search">
            <li
              v-for="product in searchResults"
              :key="product.id"
              class="quick-keys-admin__search-item"
            >
              <div>
                <strong>{{ product.name }}</strong>
                <span class="small text-muted d-block">{{ product.sku }}</span>
              </div>
              <b-button
                size="sm"
                variant="outline-success"
                :disabled="isPinned(product.id) || pinnedProducts.length >= 12"
                @click="addProduct(product)"
              >
                Add
              </b-button>
            </li>
          </ul>
          <div
            v-else-if="search.trim().length >= 2"
            class="small text-muted py-2"
          >
            No products found.
          </div>
        </section>
      </div>

      <div v-if="previewProducts.length && source === 'auto'" class="mt-4">
        <h6 class="quick-keys-admin__heading text-muted">
          Current auto suggestions on POS
        </h6>
        <div class="quick-keys-admin__preview">
          <span
            v-for="product in previewProducts"
            :key="product.id"
            class="quick-keys-admin__preview-chip"
          >
            {{ product.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";

export default {
  name: "QuickKeys",
  components: { IconButton },
  data() {
    return {
      loading: false,
      saving: false,
      source: "auto",
      pinnedProducts: [],
      previewProducts: [],
      search: "",
      searchResults: [],
      searchLoading: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId"]),
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    debouncedSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.runSearch, 300);
    },
    async load() {
      if (!this.selectedStoreId) return;
      this.loading = true;
      try {
        const data = await ApiService.get("pos/quick-keys", {
          storeId: this.selectedStoreId,
        });
        this.source = data.source || "auto";
        const products = data.products || [];
        if (this.source === "pinned") {
          this.pinnedProducts = [...products];
          this.previewProducts = [];
        } else {
          this.pinnedProducts = [];
          this.previewProducts = products;
        }
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not load quick keys",
          icon: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    async runSearch() {
      const q = this.search.trim();
      if (q.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchLoading = true;
      try {
        this.searchResults = await ApiService.get("products", {
          storeId: this.selectedStoreId,
          query: q,
          limit: 20,
        });
      } catch {
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },
    isPinned(productId) {
      return this.pinnedProducts.some((p) => p.id === productId);
    },
    addProduct(product) {
      if (this.isPinned(product.id) || this.pinnedProducts.length >= 12) return;
      this.pinnedProducts.push({
        id: product.id,
        name: product.name,
        sku: product.sku,
        unitPrice: product.unitPrice,
      });
      this.search = "";
      this.searchResults = [];
    },
    removeAt(index) {
      this.pinnedProducts.splice(index, 1);
    },
    moveUp(index) {
      if (index <= 0) return;
      const items = [...this.pinnedProducts];
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
      this.pinnedProducts = items;
    },
    moveDown(index) {
      if (index >= this.pinnedProducts.length - 1) return;
      const items = [...this.pinnedProducts];
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      this.pinnedProducts = items;
    },
    async save() {
      if (!this.selectedStoreId) return;
      this.saving = true;
      try {
        await ApiService.put("pos/quick-keys", {
          storeId: this.selectedStoreId,
          productIds: this.pinnedProducts.map((p) => p.id),
        });
        await this.load();
        toast.fire({ title: "Quick keys saved", icon: "success" });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not save quick keys",
          icon: "danger",
        });
      } finally {
        this.saving = false;
      }
    },
    clearAll() {
      this.pinnedProducts = [];
      this.save();
    },
  },
};
</script>

<style scoped>
.quick-keys-admin {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .quick-keys-admin {
    grid-template-columns: 1fr;
  }
}

.quick-keys-admin__panel {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  background: #fafafa;
}

.quick-keys-admin__heading {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.quick-keys-admin__list,
.quick-keys-admin__search {
  list-style: none;
  margin: 0;
  padding: 0;
}

.quick-keys-admin__item,
.quick-keys-admin__search-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.quick-keys-admin__item:last-child,
.quick-keys-admin__search-item:last-child {
  border-bottom: none;
}

.quick-keys-admin__order {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #1a7f4e;
  color: #fff;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-keys-admin__info {
  flex: 1;
  min-width: 0;
}

.quick-keys-admin__price {
  font-size: 0.85rem;
  white-space: nowrap;
}

.quick-keys-admin__actions {
  display: flex;
  gap: 0.25rem;
}

.quick-keys-admin__preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.quick-keys-admin__preview-chip {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
  color: #475569;
}
</style>
